"use server";

import bcrypt from "bcryptjs";
import { AuthError } from "next-auth";
import { signIn } from "@/auth";
import { db } from "@/lib/db";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit";
import { verifyTurnstile } from "@/lib/turnstile";

// Only ever redirect to a relative, same-site path - never trust the raw
// callbackUrl as-is, or a crafted "//evil.com" / "/\evil.com" value could
// send a just-registered user off-site (open redirect).
function safeCallbackUrl(callbackUrl: string | null | undefined): string {
  if (
    callbackUrl &&
    callbackUrl.startsWith("/") &&
    !callbackUrl.startsWith("//") &&
    !callbackUrl.startsWith("/\\")
  ) {
    return callbackUrl;
  }
  return "/account";
}

export async function register(
  callbackUrl: string | null,
  _prevState: string | undefined,
  formData: FormData,
) {
  const ip = await getClientIp();
  if (!checkRateLimit(`register:${ip}`, 5, 60 * 60 * 1000)) {
    return "Zbyt wiele prób założenia konta. Spróbuj ponownie później.";
  }

  if (!(await verifyTurnstile(formData, ip))) {
    return "Nie udało się zweryfikować, że nie jesteś botem. Odśwież stronę i spróbuj ponownie.";
  }

  const email = formData.get("email");
  const password = formData.get("password");

  if (typeof email !== "string" || typeof password !== "string" || !email || !password) {
    return "Podaj email i hasło.";
  }
  if (password.length < 8) {
    return "Hasło musi mieć co najmniej 8 znaków.";
  }

  const existing = await db.user.findUnique({ where: { email } });
  if (existing) {
    return "Konto z tym adresem email już istnieje.";
  }

  const passwordHash = await bcrypt.hash(password, 10);
  await db.user.create({ data: { email, passwordHash } });

  try {
    await signIn("credentials", { email, password, redirectTo: safeCallbackUrl(callbackUrl) });
  } catch (error) {
    if (error instanceof AuthError) {
      return "Konto zostało utworzone, ale automatyczne logowanie się nie powiodło. Zaloguj się ręcznie.";
    }
    throw error;
  }
}
